import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card, Row, Col, Statistic, Button, Space, Tag, Modal, Form,
  InputNumber, Select, Typography, Divider, Alert, Spin, message,
  Table, Tooltip, Descriptions, Empty, Popover,
} from 'antd';
import {
  ReloadOutlined, DeleteOutlined, PlusOutlined,
  CheckCircleOutlined, WarningOutlined, DatabaseOutlined,
  HeartOutlined, ExperimentOutlined, ApiOutlined,
  LineChartOutlined, BellOutlined, DashboardOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import api from '../services/api';
import { useFillHeight } from '../hooks/useViewportFit';
import { fmtDateTimeSec, fmtTime } from '../utils/date';
import { gray } from '../theme/tokens';

const { Title, Text, Paragraph } = Typography;

// ─── 타입 정의 ───
interface TapoStats {
  total_rows: number;
  test_rows: number;
  real_rows: number;
  test_ratio: number;
  oldest: string;
  latest: string;
  per_room: Array<{ room_id: string; count: number; oldest: string; latest: string }>;
  per_device: Array<{ device_id: string; room_id: string; count: number; is_test: boolean }>;
  alerts_open: number;
}

interface TapoHealth {
  status: string;
  ts: string;
  db: { version: string; timescaledb: string; size: string };
  hypertable: { name: string; chunks_total: number; chunks_compressed: number };
  continuous_aggregate: { name: string; rows: number };
  jobs: Array<{ job_id: number; application_name: string; interval: string }>;
}

interface CurrentReading {
  ts: string;
  site_id: string;
  room_id: string;
  device_id: string;
  nickname: string | null;
  temp_c: number;
  humidity: number;
  battery_low: boolean;
  rssi: number | null;
}

interface BucketReading {
  bucket: string;
  room_id: string;
  device_id: string;
  temp_avg: number | string;
  hum_avg: number | string;
}

interface TapoAlert {
  id: number;
  ts: string;
  room_id: string;
  device_id: string;
  severity: string;
  type: string;
  value: number;
  threshold: number;
  acked: boolean;
  resolved: boolean;
}

const ROOM_LABEL: Record<string, { name: string; icon: string; color: string }> = {
  doc: { name: '문서보관실', icon: '📁', color: '#60a5fa' },
  lab: { name: '사육실', icon: '🧬', color: '#34d399' },
  chm: { name: '시약실', icon: '🧪', color: '#a78bfa' },
};

const ROOM_ORDER = ['doc', 'lab', 'chm'] as const;

// ─── 도움말 팝오버 ───
const TapoHelpContent: React.FC<{ stats: TapoStats | null; health: TapoHealth | null }> = ({ stats, health }) => {
  const { Text } = Typography;
  return (
    <div style={{ maxWidth: 330, fontSize: 13, lineHeight: 1.6 }}>
      <div style={{ marginBottom: 8 }}>
        <Text>
          <Text strong>📡 TAPO H200</Text> 온습도 센서를 통해{' '}
          <Text strong>문서보관실 · 사육실 · 시약실</Text> 3개 실의
          환경을 실시간으로 모니터링하는 화면입니다.
        </Text>
      </div>
      <Divider style={{ margin: '8px 0' }} />
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <div>
          <div style={{ fontSize: 11, color: '#888' }}>⏱ UI 자동갱신 주기</div>
          <Text>현재값 · 알림: <Text strong>15초</Text> 폴링 / 추이 차트: 수동 재조회</Text>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#888' }}>📊 추이 집계 단위</div>
          <Text>5분 평균 버킷 (TimescaleDB Continuous Aggregate)</Text>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#888' }}>🌐 데이터 소스</div>
          <Text>OCI TAPO API (BFF 프록시, 토큰 서버 보관)</Text>
        </div>
        {stats?.oldest && (
          <div>
            <div style={{ fontSize: 11, color: '#888' }}>📅 수집 시작일시 (최오래된 레코드)</div>
            <Text strong>{fmtDateTimeSec(stats.oldest)}</Text>
          </div>
        )}
        {stats?.latest && (
          <div>
            <div style={{ fontSize: 11, color: '#888' }}>🕐 최신 수집일시</div>
            <Text strong>{fmtDateTimeSec(stats.latest)}</Text>
          </div>
        )}
        {health?.jobs && health.jobs.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: '#888' }}>⚙️ TimescaleDB 자동화 잡</div>
            {health.jobs.map(j => (
              <div key={j.job_id} style={{ fontSize: 12 }}>
                <Text code style={{ fontSize: 11 }}>{j.application_name}</Text>
                <Text type="secondary"> — {j.interval}</Text>
              </div>
            ))}
          </div>
        )}
      </Space>
    </div>
  );
};

// 습도 위험도 5단계 (tapo.html과 동일)
function humidityLevel(h: number): { label: string; color: string; ico: string } {
  if (h < 30)  return { label: '매우건조', color: '#fbbf24', ico: '🏜️' };
  if (h < 45)  return { label: '건조',     color: '#fde68a', ico: '🌵' };
  if (h < 60)  return { label: '적정',     color: '#34d399', ico: '💧' };
  if (h < 75)  return { label: '다습',     color: '#60a5fa', ico: '🌧️' };
  return       { label: '매우다습', color: '#a78bfa', ico: '🌊' };
}

const TapoManagementPage: React.FC = () => {
  // ─── 기존 상태 ───
  /* 문서 세로스크롤 0 정책 — 헤더는 고정하고 본문만 내부 스크롤 */
  const { ref: fillRef, height: fillHeight } = useFillHeight(320);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<TapoHealth | null>(null);
  const [stats, setStats] = useState<TapoStats | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [insertModalOpen, setInsertModalOpen] = useState(false);
  const [insertSubmitting, setInsertSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // ─── 데이터 조회용 상태 ───
  const [currentData, setCurrentData] = useState<CurrentReading[]>([]);
  const [currentLoading, setCurrentLoading] = useState(false);
  const [currentErr, setCurrentErr] = useState<string | null>(null);
  const [readings, setReadings] = useState<BucketReading[]>([]);
  const [readingsLoading, setReadingsLoading] = useState(false);
  const [alerts, setAlerts] = useState<TapoAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const pollingTimerRef = useRef<number | null>(null);

  const [form] = Form.useForm();

  // ─── 데이터 로드 ───
  const loadAdmin = useCallback(async () => {
    setLoading(true);
    setHealthError(null);
    try {
      const [h, s] = await Promise.all([
        api.get<TapoHealth>('/admin/tapo/health'),
        api.get<TapoStats>('/admin/tapo/stats'),
      ]);
      setHealth(h.data);
      setStats(s.data);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '조회 실패';
      setHealthError(detail);
      message.error(`TAPO API 조회 실패: ${detail}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // 15초 polling 대상: 실시간 현재값 + 알림
  const loadCurrent = useCallback(async (showSpinner = true) => {
    if (showSpinner) setCurrentLoading(true);
    setCurrentErr(null);
    try {
      const r = await api.get<CurrentReading[]>('/admin/tapo/current');
      setCurrentData(r.data);
      setLastSync(new Date());
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '조회 실패';
      setCurrentErr(detail);
    } finally {
      setCurrentLoading(false);
    }
  }, []);

  const loadAlerts = useCallback(async (showSpinner = true) => {
    if (showSpinner) setAlertsLoading(true);
    try {
      const r = await api.get<TapoAlert[]>('/admin/tapo/alerts');
      setAlerts(r.data || []);
    } catch {
      // alerts 테이블 없을 수 있음 — 조용히 무시
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  // 24시간 추이 (5분 버킷)
  const loadReadings = useCallback(async () => {
    setReadingsLoading(true);
    try {
      const r = await api.get<BucketReading[]>('/admin/tapo/readings', {
        params: { since: '24h', bucket: '5min', limit: 2000 },
      });
      setReadings(r.data);
    } catch (e: any) {
      message.error(`추이 조회 실패: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setReadingsLoading(false);
    }
  }, []);

  // 초기 + polling 시작
  useEffect(() => {
    loadAdmin();
    loadCurrent();
    loadAlerts();
    loadReadings();

    pollingTimerRef.current = window.setInterval(() => {
      loadCurrent(false);
      loadAlerts(false);
    }, 15000);
    return () => {
      if (pollingTimerRef.current) window.clearInterval(pollingTimerRef.current);
    };
  }, [loadAdmin, loadCurrent, loadAlerts, loadReadings]);

  const handleRefreshAll = useCallback(() => {
    loadAdmin();
    loadCurrent();
    loadAlerts();
    loadReadings();
  }, [loadAdmin, loadCurrent, loadAlerts, loadReadings]);

  // ─── 테스트 데이터 입력 ───
  const handleInsert = async (values: any) => {
    setInsertSubmitting(true);
    try {
      const r = await api.post('/admin/tapo/test-data', {
        count_per_room: values.count_per_room,
        rooms: values.rooms,
        spread_hours: values.spread_hours,
        interval_minutes: 5,
      });
      message.success(`테스트 데이터 ${r.data.inserted}건 입력 완료`);
      setInsertModalOpen(false);
      form.resetFields();
      await Promise.all([loadAdmin(), loadCurrent(), loadReadings()]);
    } catch (e: any) {
      message.error(`입력 실패: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setInsertSubmitting(false);
    }
  };

  // ─── 테스트 데이터 삭제 ───
  const handleDelete = async () => {
    setDeleteSubmitting(true);
    try {
      const r = await api.delete('/admin/tapo/test-data');
      message.success(`테스트 데이터 ${r.data.deleted}건 삭제 완료`);
      setDeleteConfirm(false);
      await Promise.all([loadAdmin(), loadCurrent(), loadReadings()]);
    } catch (e: any) {
      message.error(`삭제 실패: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const healthOk = health?.status === 'ok' && !healthError;

  // ─── 실시간 KPI: room_id별 최신 (실 device 우선, SIM도 포함하되 표시) ───
  const currentByRoom = useMemo(() => {
    const map: Record<string, CurrentReading | undefined> = {};
    for (const r of currentData) {
      const cur = map[r.room_id];
      if (!cur || new Date(r.ts) > new Date(cur.ts)) {
        map[r.room_id] = r;
      }
    }
    return map;
  }, [currentData]);

  // ─── 24h 추이 차트 옵션 ───
  const trendOption = useMemo(() => {
    if (!readings.length) return null;

    // room_id별로 시간순 정렬된 시리즈 생성
    const byRoom: Record<string, { time: string; t: number; h: number }[]> = {};
    for (const r of readings) {
      if (!byRoom[r.room_id]) byRoom[r.room_id] = [];
      byRoom[r.room_id].push({
        time: r.bucket,
        t: Number(r.temp_avg),
        h: Number(r.hum_avg),
      });
    }
    // 같은 device_id 여러개 합쳐서 평균
    Object.keys(byRoom).forEach(rid => {
      const grouped: Record<string, { ts: number; ts_cnt: number; hs: number; hs_cnt: number }> = {};
      for (const p of byRoom[rid]) {
        if (!grouped[p.time]) grouped[p.time] = { ts: 0, ts_cnt: 0, hs: 0, hs_cnt: 0 };
        grouped[p.time].ts += p.t;  grouped[p.time].ts_cnt += 1;
        grouped[p.time].hs += p.h;  grouped[p.time].hs_cnt += 1;
      }
      byRoom[rid] = Object.entries(grouped)
        .sort(([a],[b]) => a.localeCompare(b))
        .map(([time,v]) => ({
          time,
          t: +(v.ts / v.ts_cnt).toFixed(1),
          h: +(v.hs / v.hs_cnt).toFixed(1),
        }));
    });

    const series: any[] = [];
    ROOM_ORDER.forEach(rid => {
      const info = ROOM_LABEL[rid];
      const points = byRoom[rid] || [];
      series.push({
        name: `${info.icon} ${info.name} 온도`,
        type: 'line', smooth: true, symbol: 'none',
        yAxisIndex: 0,
        lineStyle: { width: 2.2, color: info.color },
        itemStyle: { color: info.color },
        data: points.map(p => [p.time, p.t]),
      });
      series.push({
        name: `${info.icon} ${info.name} 습도`,
        type: 'line', smooth: true, symbol: 'none',
        yAxisIndex: 1,
        lineStyle: { width: 1.5, color: info.color, type: 'dashed', opacity: 0.85 },
        itemStyle: { color: info.color },
        data: points.map(p => [p.time, p.h]),
      });
    });

    return {
      tooltip: { trigger: 'axis' },
      legend: { top: 4, textStyle: { fontSize: 11 } },
      grid: { left: 50, right: 55, top: 38, bottom: 36 },
      xAxis: { type: 'time' },
      yAxis: [
        { type: 'value', name: '🌡 °C', position: 'left', min: 'dataMin', max: 'dataMax',
          nameTextStyle: { color: '#f59e0b', fontSize: 11 }, axisLabel: { fontSize: 10 } },
        { type: 'value', name: '💧 %RH', position: 'right', min: 0, max: 100,
          nameTextStyle: { color: '#3b82f6', fontSize: 11 }, axisLabel: { fontSize: 10 } },
      ],
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { type: 'slider', height: 18, bottom: 4 },
      ],
      series,
    };
  }, [readings]);

  return (
    /* 루트에 패딩을 주지 않는다 — Content 의 20px 패딩 밖에 여백을 더하면 문서 스크롤이 생긴다 */
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Space align="center" size={6}>
          <Title level={4} style={{ margin: 0, color: gray[800] }}>
            <ApiOutlined /> TAPO 관리
          </Title>
        </Space>
        <Space>
          {/* ? 는 우측 영역 맨 왼쪽 — 대시보드·시험일정총괄표와 동일 규칙 */}
          <Popover
            title={<Space><QuestionCircleOutlined /> TAPO 관리 화면 안내</Space>}
            content={<TapoHelpContent stats={stats} health={health} />}
            trigger="click"
            placement="bottomRight"
          >
            <QuestionCircleOutlined
              style={{ fontSize: 16, color: '#9ca3af', cursor: 'pointer' }}
            />
          </Popover>
          {lastSync && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              마지막 동기화: {fmtTime(lastSync)}
            </Text>
          )}
          <Button icon={<ReloadOutlined />} onClick={handleRefreshAll} loading={loading}>
            새로고침
          </Button>
        </Space>
      </Space>

      {healthError && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message="TAPO API 도달 실패" description={healthError} />
      )}

      {/* 아래 카드들은 내용이 길어 뷰포트를 넘는다 → 실측 높이 안에서만 스크롤시킨다.
          (Row 음수 gutter 가 가로 스크롤바를 만들므로 overflowX 는 hidden) */}
      <div ref={fillRef} style={{ height: fillHeight, overflowY: 'auto', overflowX: 'hidden' }}>

      {/* ════════════════════════════════════════════════════════════════
          ① 실시간 현재값 KPI — 3개 방, 15초 자동 polling
          ════════════════════════════════════════════════════════════════ */}
      <Card
        title={
          <Space>
            <DashboardOutlined />
            <span>실시간 현재값 (15초 자동갱신)</span>
            {currentErr && <Tag color="error">오류</Tag>}
          </Space>
        }
        size="small"
        style={{ marginBottom: 12 }}
        loading={currentLoading && !currentData.length}
      >
        <Row gutter={[12, 12]}>
          {ROOM_ORDER.map(rid => {
            const info = ROOM_LABEL[rid];
            const r = currentByRoom[rid];
            const h = r ? Math.round(r.humidity) : null;
            const t = r ? +Number(r.temp_c).toFixed(1) : null;
            const lvl = h != null ? humidityLevel(h) : null;
            const isStale = r ? (Date.now() - new Date(r.ts).getTime()) > 5 * 60 * 1000 : true;
            return (
              <Col xs={24} md={8} key={rid}>
                <Card
                  size="small"
                  bordered
                  style={{
                    background: lvl
                      ? `linear-gradient(135deg, ${lvl.color}22 0%, transparent 70%)`
                      : undefined,
                    borderLeft: `4px solid ${info.color}`,
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                    <Space direction="vertical" size={2}>
                      <Text style={{ fontSize: 13, color: '#666' }}>
                        <span style={{ fontSize: 18, marginRight: 6 }}>{info.icon}</span>
                        {info.name}
                      </Text>
                      <Text style={{ fontSize: 11, color: '#999' }}>
                        {r?.device_id || '-'}
                      </Text>
                    </Space>
                    {lvl && (
                      <Tag color={lvl.color === '#fde68a' || lvl.color === '#fbbf24' ? 'warning'
                                 : lvl.color === '#34d399' ? 'success'
                                 : lvl.color === '#a78bfa' ? 'purple' : 'processing'}>
                        {lvl.ico} {lvl.label}
                      </Tag>
                    )}
                  </Space>
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'baseline', gap: 14 }}>
                    {h != null ? (
                      <>
                        <div>
                          <span style={{ fontSize: 11, color: '#777' }}>💧 습도</span>
                          <div style={{ fontSize: 36, fontWeight: 800, color: lvl?.color || '#444', lineHeight: 1 }}>
                            {h}<span style={{ fontSize: 14, fontWeight: 600, marginLeft: 4 }}>%RH</span>
                          </div>
                        </div>
                        <Divider type="vertical" style={{ height: 42 }} />
                        <div>
                          <span style={{ fontSize: 11, color: '#777' }}>🌡 온도</span>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#555', lineHeight: 1.1 }}>
                            {t}<span style={{ fontSize: 12, marginLeft: 2 }}>°C</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="데이터 없음" />
                    )}
                  </div>
                  {r && (
                    <div style={{ marginTop: 8, fontSize: 11, color: isStale ? '#cf1322' : '#999' }}>
                      마지막 측정: {fmtDateTimeSec(r.ts)}
                      {isStale && ' (5분 이상 지연)'}
                    </div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* ════════════════════════════════════════════════════════════════
          ② 24h 추이 차트 + ③ 알림 패널
          ════════════════════════════════════════════════════════════════ */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} lg={16}>
          <Card
            title={<Space><LineChartOutlined /> 24시간 추이 (5분 평균)</Space>}
            size="small"
            loading={readingsLoading && !readings.length}
            extra={
              <Button size="small" icon={<ReloadOutlined />} onClick={loadReadings}
                      loading={readingsLoading}>
                재조회
              </Button>
            }
          >
            {trendOption ? (
              <ReactECharts option={trendOption} style={{ height: 320 }} notMerge={true} />
            ) : (
              <Empty description="추이 데이터 없음" style={{ padding: 60 }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <BellOutlined />
                <span>최근 알림</span>
                <Tag color={alerts.length > 0 ? 'error' : 'success'}>{alerts.length}</Tag>
              </Space>
            }
            size="small"
            loading={alertsLoading && !alerts.length}
          >
            {alerts.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                     description="미해결 알림 없음" style={{ padding: 30 }} />
            ) : (
              <Table
                size="small" rowKey="id" pagination={false}
                dataSource={alerts.slice(0, 8)}
                columns={[
                  { title: '시각', dataIndex: 'ts', width: 80,
                    render: (v) => fmtTime(v) },
                  { title: '실', dataIndex: 'room_id', width: 70,
                    render: (rid) => ROOM_LABEL[rid]
                      ? <span>{ROOM_LABEL[rid].icon} {ROOM_LABEL[rid].name}</span>
                      : rid },
                  { title: '타입', dataIndex: 'type', width: 60 },
                  { title: '값', dataIndex: 'value', width: 50, align: 'right',
                    render: (v) => Number(v).toFixed(1) },
                  { title: '심각도', dataIndex: 'severity',
                    render: (s) => <Tag color={
                      s === 'critical' ? 'error' : s === 'warning' ? 'warning' : 'default'
                    }>{s}</Tag> },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* ════════════════════════════════════════════════════════════════
          ④ 기존 운영 정보 (헬스 / 통계 / 실별 / 디바이스)
          ════════════════════════════════════════════════════════════════ */}
      <Divider plain style={{ margin: '8px 0 12px' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>운영 정보</Text>
      </Divider>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={8}>
          <Card title={<Space><HeartOutlined /> API · DB 상태</Space>}
                size="small" loading={loading && !health}>
            {health && (
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="상태">
                  {healthOk
                    ? <Tag color="success" icon={<CheckCircleOutlined />}>정상</Tag>
                    : <Tag color="error" icon={<WarningOutlined />}>이상</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="PostgreSQL">
                  <Text style={{ fontSize: 11 }}>{health.db.version.split(',')[0]}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="TimescaleDB">
                  <Tag color="cyan">v{health.db.timescaledb}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="DB 크기">{health.db.size}</Descriptions.Item>
                <Descriptions.Item label="Hypertable 청크">
                  {health.hypertable.chunks_total}개 (압축: {health.hypertable.chunks_compressed}개)
                </Descriptions.Item>
                <Descriptions.Item label="readings_5m">
                  {health.continuous_aggregate.rows.toLocaleString()} 행
                </Descriptions.Item>
                <Descriptions.Item label="자동화 잡">{health.jobs.length}개</Descriptions.Item>
              </Descriptions>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<Space><DatabaseOutlined /> 전체 통계</Space>}
                size="small" loading={loading && !stats}>
            {stats && (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="총 행수" value={stats.total_rows}
                               valueStyle={{ fontSize: 18 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="알람(미해결)" value={stats.alerts_open}
                               valueStyle={{ fontSize: 18,
                                 color: stats.alerts_open > 0 ? '#cf1322' : '#3f8600' }} />
                  </Col>
                </Row>
                <Divider style={{ margin: '4px 0' }} />
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="실데이터" value={stats.real_rows}
                               valueStyle={{ fontSize: 16, color: '#3f8600' }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="테스트 데이터" value={stats.test_rows}
                               suffix={<Text type="secondary" style={{ fontSize: 11 }}>
                                 ({(stats.test_ratio * 100).toFixed(1)}%)</Text>}
                               valueStyle={{ fontSize: 16, color: '#d48806' }} />
                  </Col>
                </Row>
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ fontSize: 11.5, color: '#666' }}>
                  <div>최오래된: {fmtDateTimeSec(stats.oldest)}</div>
                  <div>최신: {fmtDateTimeSec(stats.latest)}</div>
                </div>
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<Space><ExperimentOutlined /> 실별 분포</Space>}
                size="small" loading={loading && !stats}>
            {stats && stats.per_room.map(r => {
              const info = ROOM_LABEL[r.room_id] || { name: r.room_id, icon: '❓', color: '#999' };
              const pct = stats.total_rows > 0
                ? (r.count / stats.total_rows * 100).toFixed(1) : '0.0';
              return (
                <div key={r.room_id} style={{ marginBottom: 8 }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <span style={{ fontSize: 16 }}>{info.icon}</span>
                      <Text strong>{info.name}</Text>
                    </Space>
                    <Tag color={info.color} style={{ color: '#fff', borderColor: info.color }}>
                      {r.count.toLocaleString()} ({pct}%)
                    </Tag>
                  </Space>
                </div>
              );
            })}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="디바이스별 데이터" size="small" loading={loading && !stats}>
            {stats && (
              <Table
                size="small" rowKey="device_id" pagination={false}
                dataSource={stats.per_device}
                columns={[
                  { title: '디바이스 ID', dataIndex: 'device_id',
                    render: (id, row: any) => (
                      <Space>
                        <Text>{id}</Text>
                        {row.is_test && <Tag color="orange">테스트</Tag>}
                      </Space>
                    ),
                  },
                  { title: '실', dataIndex: 'room_id', width: 140,
                    render: (rid) => {
                      const info = ROOM_LABEL[rid];
                      return info ? <span>{info.icon} {info.name}</span> : rid;
                    },
                  },
                  { title: '행 수', dataIndex: 'count', align: 'right', width: 120,
                    render: (c) => c.toLocaleString() },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      </div>

      {/* ─── 테스트 데이터 입력 모달 (기존 유지) ─── */}
      <Modal
        title={<Space><PlusOutlined /> 테스트 데이터 입력</Space>}
        open={insertModalOpen}
        onCancel={() => setInsertModalOpen(false)}
        footer={null}
        width={520}
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="device_id에 자동으로 TEST 접미사가 붙어 실 데이터와 구분됩니다."
          description="예: T315-DOC-TEST, T315-LAB-TEST, T315-CHM-TEST. 일중 사인파 패턴 + 노이즈로 자연스러운 측정값이 생성됩니다." />
        <Form form={form} layout="vertical" onFinish={handleInsert}
              initialValues={{
                count_per_room: 10,
                rooms: ['doc', 'lab', 'chm'],
                spread_hours: 24,
              }}>
          <Form.Item name="rooms" label="대상 실"
                     rules={[{ required: true, message: '최소 1개 실 선택' }]}>
            <Select mode="multiple" placeholder="실 선택"
                    options={Object.entries(ROOM_LABEL).map(([k, v]) => ({
                      value: k, label: `${v.icon} ${v.name}` }))} />
          </Form.Item>
          <Form.Item name="count_per_room" label="실당 생성 건수"
                     rules={[{ required: true }]}>
            <InputNumber min={1} max={500} style={{ width: '100%' }} addonAfter="건/실" />
          </Form.Item>
          <Form.Item name="spread_hours" label="과거 N시간에 분산"
                     rules={[{ required: true }]}>
            <InputNumber min={1} max={720} style={{ width: '100%' }} addonAfter="시간" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={insertSubmitting}>
                입력 실행
              </Button>
              <Button onClick={() => setInsertModalOpen(false)}>취소</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ─── 테스트 데이터 삭제 확인 (기존 유지) ─── */}
      <Modal
        title={<Space><WarningOutlined style={{ color: '#cf1322' }} /> 테스트 데이터 삭제 확인</Space>}
        open={deleteConfirm}
        onCancel={() => setDeleteConfirm(false)}
        onOk={handleDelete}
        okText="삭제 실행"
        okButtonProps={{ danger: true, loading: deleteSubmitting }}
        cancelText="취소"
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="다음 패턴의 device_id를 가진 데이터만 삭제됩니다."
          description={
            <Space direction="vertical" size={4}>
              <Text code>device_id LIKE '%TEST%'</Text>
              <Text code>device_id LIKE '%SIM%'</Text>
              <Text code>device_id LIKE '%API%'</Text>
              <Text strong type="danger">실제 H200 센서 데이터(T315-XXX 같은 별칭)는 영향 없음.</Text>
            </Space>
          } />
        {stats && (
          <Paragraph>
            현재 테스트 데이터: <Text strong>{stats.test_rows.toLocaleString()}건</Text>
            ({(stats.test_ratio * 100).toFixed(1)}%) 일괄 삭제됩니다.
          </Paragraph>
        )}
      </Modal>
    </div>
  );
};

export default TapoManagementPage;
